import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Edit, Plus, CheckCircle, XCircle, Trash2, RefreshCw, AlertCircle, Clock, Pencil } from 'lucide-react';
import { stcBinGrpRelationService, StcBinGrpRelationDto, CreateStcBinGrpRelationRequest } from '../../../../../services/stcBinGrpRelationService';
import { stcBinGrpService, StcBinGrpDto } from '../../../../../services/stcBinGrpService';
import { useAuth } from '../../../../../contexts/AuthContext';
import { WarehouseTable } from '../../../../shared/table';
import { createColumnFactory } from '../../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, TableAction, ExpandableRowConfig, TableDataRow } from '../../../../shared/table/types/table';
import { BinGrpRelationSidePanel } from './BinGrpRelationSidePanel';
import { BinGrpRelationCreateModal } from './BinGrpRelationCreateModal';

interface BinGrpRelationDataTableProps {
  refreshTrigger?: number;
}

// Hode-rad: én per master-gruppe
interface MasterGroupRow extends TableDataRow {
  id: number;
  masterBinGrpNo: number;
  masterDescription: string;
  commonAvailable: boolean;
  childCount: number;
  companyNo: string;
  // Audit (fra StcBinGrpDto)
  createdBy: string;
  created: string;
  updatedBy: string;
  lastUpdate: string;
}

// Linje-rad: én per barn-relasjon
interface ChildRelationRow extends TableDataRow {
  id: number;
  relationId: number;
  childBinGrpNo: number;
  childDescription: string;
  companyNo: string;
  masterBinGrpNo: number;
  // Audit (fra StcBinGrpRelationDto)
  createdBy: string;
  created: string;
  updatedBy: string;
  lastUpdate: string;
}

// ─── Hjelpe-komponent for dato-formatering ───────────────────────────────────
const formatDate = (val?: string | null): string => {
  if (!val) return '–';
  try {
    return new Date(val).toLocaleDateString('nb-NO', {
      day: '2-digit', month: '2-digit', year: 'numeric'
    });
  } catch {
    return val;
  }
};

// ─── Inline sub-gruppe-editor (brukes i renderDetail) ───────────────────────
interface SubGroupDetailProps {
  masterRow: MasterGroupRow;
  childRows: ChildRelationRow[];
  binGroups: StcBinGrpDto[];
  masterNos: number[];
  onRefresh: () => void;
}

const SubGroupDetail: React.FC<SubGroupDetailProps> = ({ masterRow, childRows, binGroups, masterNos, onRefresh }) => {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [addingNew, setAddingNew] = useState(false);
  const [newChildNo, setNewChildNo] = useState<number>(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Audit-kolonner skjult som standard
  const [showAudit, setShowAudit] = useState(false);

  const usedChildNos = childRows.map(r => r.childBinGrpNo);
  // Tilgjengelige grupper for ny sub (ekskluder master selv, allerede brukte barn, og alle andre mastere)
  const availableForNew = binGroups.filter(
    g => g.binGrpNo !== masterRow.masterBinGrpNo
      && !usedChildNos.includes(g.binGrpNo)
      && !masterNos.includes(g.binGrpNo)
  );

  const getBinGrpLabel = (no: number) => {
    const g = binGroups.find(g => g.binGrpNo === no);
    return g ? `${no} - ${g.description1}` : String(no);
  };

  const handleDelete = async (row: ChildRelationRow) => {
    if (!window.confirm(`Slette relasjonen ${getBinGrpLabel(row.childBinGrpNo)}?`)) return;
    setBusy(true);
    setError('');
    try {
      await stcBinGrpRelationService.delete(row.companyNo, row.masterBinGrpNo, row.childBinGrpNo);
      onRefresh();
    } catch (e: any) {
      setError(e.message || 'Feil ved sletting');
    } finally {
      setBusy(false);
    }
  };

  const handleStartEdit = (row: ChildRelationRow) => {
    setEditingId(row.id);
    setEditValue(row.childBinGrpNo);
    setError('');
  };

  const handleSaveEdit = async (row: ChildRelationRow) => {
    if (editValue === row.childBinGrpNo) { setEditingId(null); return; }
    if (editValue === masterRow.masterBinGrpNo) { setError('Sub-gruppe kan ikke være samme som master'); return; }
    if (usedChildNos.filter(n => n !== row.childBinGrpNo).includes(editValue)) {
      setError('Denne sub-gruppen er allerede lagt til'); return;
    }
    if (masterNos.includes(editValue)) {
      setError('Denne gruppen er allerede registrert som Master og kan ikke være Sub'); return;
    }
    setBusy(true);
    setError('');
    try {
      await stcBinGrpRelationService.delete(row.companyNo, row.masterBinGrpNo, row.childBinGrpNo);
      const req: CreateStcBinGrpRelationRequest = {
        companyNo: row.companyNo,
        masterBinGrpNo: row.masterBinGrpNo,
        childBinGrpNo: editValue,
      };
      await stcBinGrpRelationService.create(req);
      setEditingId(null);
      onRefresh();
    } catch (e: any) {
      setError(e.message || 'Feil ved endring');
    } finally {
      setBusy(false);
    }
  };

  const handleAddNew = async () => {
    if (newChildNo === 0) { setError('Velg en sub-gruppe'); return; }
    if (newChildNo === masterRow.masterBinGrpNo) { setError('Sub-gruppe kan ikke være samme som master'); return; }
    if (usedChildNos.includes(newChildNo)) { setError('Denne sub-gruppen er allerede lagt til'); return; }
    if (masterNos.includes(newChildNo)) { setError('Denne gruppen er allerede registrert som Master og kan ikke være Sub'); return; }
    setBusy(true);
    setError('');
    try {
      const req: CreateStcBinGrpRelationRequest = {
        companyNo: masterRow.companyNo,
        masterBinGrpNo: masterRow.masterBinGrpNo,
        childBinGrpNo: newChildNo,
      };
      await stcBinGrpRelationService.create(req);
      setAddingNew(false);
      setNewChildNo(0);
      onRefresh();
    } catch (e: any) {
      setError(e.message || 'Feil ved opprettelse');
    } finally {
      setBusy(false);
    }
  };

  // Antall kolonner totalt (brukes til colSpan)
  const totalCols = 3 + (showAudit ? 4 : 0); // handlinger + sub nr. + beskrivelse [+ 4 audit]

  return (
    <div className="px-6 py-3 bg-slate-50 border-t border-slate-200">
      {error && (
        <div className="flex items-center gap-2 mb-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-1.5">
          <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
          {error}
        </div>
      )}

      <table className="w-full text-xs">
        <thead>
          <tr className="text-slate-500 border-b border-slate-200">
            {/* Aksjonskolonne til venstre */}
            <th className="text-left py-1.5 font-medium w-28">
              <button
                onClick={() => setShowAudit(v => !v)}
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded transition-colors focus:outline-none ${
                  showAudit
                    ? 'bg-orange-100 text-orange-700'
                    : 'text-white hover:bg-orange-50 hover:text-orange-600 active:bg-orange-100 active:text-orange-700'
                }`}
                title={showAudit ? 'Skjul audit-kolonner' : 'Vis audit-kolonner'}
              >
                <Clock className="w-3 h-3" />
                {showAudit ? 'Skjul audit' : 'Vis audit'}
              </button>
            </th>
            <th className="text-left py-1.5 font-medium w-40">Sub Lokasjonsgruppe</th>
            <th className="text-left py-1.5 font-medium">Beskrivelse</th>
            {showAudit && (
              <>
                <th className="text-left py-1.5 font-medium w-28">Opprettet av</th>
                <th className="text-left py-1.5 font-medium w-24">Opprettet</th>
                <th className="text-left py-1.5 font-medium w-28">Oppdatert av</th>
                <th className="text-left py-1.5 font-medium w-24">Sist oppdatert</th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {childRows.map(row => (
            <tr key={row.id} className="border-b border-slate-100 hover:bg-white transition-colors">
              {/* Aksjoner til venstre */}
              <td className="py-1.5 pr-2">
                <div className="flex items-center gap-1">
                  {editingId === row.id ? (
                    <>
                      <button
                        onClick={() => handleSaveEdit(row)}
                        disabled={busy}
                        className="px-2 py-0.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                      >
                        {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Lagre'}
                      </button>
                      <button
                        onClick={() => { setEditingId(null); setError(''); }}
                        className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Avbryt
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => handleStartEdit(row)}
                        disabled={busy}
                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded transition-colors disabled:opacity-30"
                        title="Rediger"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(row)}
                        disabled={busy}
                        className="p-1 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 disabled:opacity-50 transition-colors"
                        title="Slett"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </>
                  )}
                </div>
              </td>
              <td className="py-1.5 pr-3">
                {editingId === row.id ? (
                  <select
                    value={editValue}
                    onChange={e => setEditValue(parseInt(e.target.value) || 0)}
                    className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 bg-white"
                    autoFocus
                  >
                    <option value={0}>Velg...</option>
                    {/* Vis nåværende + tilgjengelige */}
                    <option value={row.childBinGrpNo}>{getBinGrpLabel(row.childBinGrpNo)}</option>
                    {binGroups
                      .filter(g =>
                        g.binGrpNo !== masterRow.masterBinGrpNo &&
                        g.binGrpNo !== row.childBinGrpNo &&
                        !usedChildNos.includes(g.binGrpNo) &&
                        !masterNos.includes(g.binGrpNo)
                      )
                      .map(g => (
                        <option key={g.id} value={g.binGrpNo}>
                          {g.binGrpNo} - {g.description1}
                        </option>
                      ))}
                  </select>
                ) : (
                  <span className="font-mono text-slate-700">{row.childBinGrpNo}</span>
                )}
              </td>
              <td className="py-1.5 pr-3 text-slate-600">
                {editingId === row.id
                  ? binGroups.find(g => g.binGrpNo === editValue)?.description1 || ''
                  : row.childDescription}
              </td>
              {showAudit && (
                <>
                  <td className="py-1.5 pr-3 text-slate-500">{row.createdBy || '–'}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{formatDate(row.created)}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{row.updatedBy || '–'}</td>
                  <td className="py-1.5 pr-3 text-slate-500">{formatDate(row.lastUpdate)}</td>
                </>
              )}
            </tr>
          ))}

          {/* Ny sub-rad */}
          {addingNew ? (
            <tr className="border-b border-blue-100 bg-blue-50">
              <td className="py-1.5 pr-2">
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleAddNew}
                    disabled={busy || newChildNo === 0}
                    className="px-2 py-0.5 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                  >
                    {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : 'Legg til'}
                  </button>
                  <button
                    onClick={() => { setAddingNew(false); setNewChildNo(0); setError(''); }}
                    className="px-2 py-0.5 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Avbryt
                  </button>
                </div>
              </td>
              <td colSpan={totalCols - 1} className="py-1.5 pr-3">
                <select
                  value={newChildNo}
                  onChange={e => setNewChildNo(parseInt(e.target.value) || 0)}
                  className="w-full px-2 py-1 text-xs border border-blue-300 rounded focus:ring-1 focus:ring-blue-500 bg-white"
                  autoFocus
                >
                  <option value={0}>Velg sub-gruppe...</option>
                  {availableForNew.map(g => (
                    <option key={g.id} value={g.binGrpNo}>
                      {g.binGrpNo} - {g.description1}
                    </option>
                  ))}
                </select>
              </td>
            </tr>
          ) : (
            <tr>
              <td colSpan={totalCols} className="pt-2 pb-1">
                <button
                  onClick={() => { setAddingNew(true); setError(''); }}
                  disabled={busy || availableForNew.length === 0}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Legg til sub-gruppe
                  {availableForNew.length === 0 && (
                    <span className="text-gray-400">(ingen tilgjengelige)</span>
                  )}
                </button>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
};
// ─────────────────────────────────────────────────────────────────────────────

export const BinGrpRelationDataTable: React.FC<BinGrpRelationDataTableProps> = ({
  refreshTrigger
}) => {
  const { user } = useAuth();
  const companyNo = user?.companyNo || '';

  const [relations, setRelations] = useState<StcBinGrpRelationDto[]>([]);
  const [binGroups, setBinGroups] = useState<StcBinGrpDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => { loadData(); }, [companyNo]);

  useEffect(() => {
    if (refreshTrigger && refreshTrigger > 0) loadData();
  }, [refreshTrigger]);

  const loadData = useCallback(async () => {
    if (!companyNo) return;
    setIsLoading(true);
    setError(null);
    try {
      const [rels, groups] = await Promise.all([
        stcBinGrpRelationService.getCurrent().catch(() =>
          stcBinGrpRelationService.getAll().catch(() => [])
        ),
        stcBinGrpService.getCurrent().catch(() =>
          stcBinGrpService.getAll().catch(() => [])
        )
      ]);
      setRelations(rels);
      setBinGroups(groups);
    } catch (err: any) {
      setError('Kunne ikke laste lokasjonsgrupperelasjon. Prøv igjen.');
    } finally {
      setIsLoading(false);
    }
  }, [companyNo]);

  const getBinGrp = useCallback((no: number) =>
    binGroups.find(g => g.binGrpNo === no), [binGroups]);

  const getBinGrpDescription = useCallback((no: number) =>
    getBinGrp(no)?.description1 || '', [getBinGrp]);

  // Hode-rader (inkl. audit-felter fra StcBinGrpDto)
  const masterRows: MasterGroupRow[] = useMemo(() => {
    const masterNos = Array.from(new Set(relations.map(r => r.masterBinGrpNo))).sort((a, b) => a - b);
    return masterNos.map(masterNo => {
      const grp = getBinGrp(masterNo);
      const children = relations.filter(r => r.masterBinGrpNo === masterNo);
      return {
        id: masterNo,
        masterBinGrpNo: masterNo,
        masterDescription: grp?.description1 || '',
        commonAvailable: grp?.commonAvailable ?? false,
        childCount: children.length,
        companyNo: children[0]?.companyNo || companyNo,
        // Audit fra StcBinGrpDto
        createdBy: grp?.createdBy || '',
        created: grp?.created || '',
        updatedBy: grp?.updatedBy || '',
        lastUpdate: grp?.lastUpdate || '',
      };
    });
  }, [relations, getBinGrp, companyNo]);

  // Barn-rader for en master (inkl. audit-felter fra StcBinGrpRelationDto)
  const getChildRows = useCallback((masterRow: MasterGroupRow): ChildRelationRow[] =>
    relations
      .filter(r => r.masterBinGrpNo === masterRow.masterBinGrpNo)
      .map(r => ({
        id: r.id,
        relationId: r.id,
        childBinGrpNo: r.childBinGrpNo,
        childDescription: getBinGrpDescription(r.childBinGrpNo),
        companyNo: r.companyNo,
        masterBinGrpNo: r.masterBinGrpNo,
        // Audit fra StcBinGrpRelationDto
        createdBy: r.createdBy || '',
        created: r.created || '',
        updatedBy: r.updatedBy || '',
        lastUpdate: r.lastUpdate || '',
      })),
    [relations, getBinGrpDescription]
  );

  // Hode-kolonner
  const columnFactory = createColumnFactory<MasterGroupRow>();

  const masterColumns: ColumnDefinition<MasterGroupRow>[] = useMemo(() => [
    columnFactory.createNumberColumn('masterBinGrpNo', 'masterBinGrpNo', 'Master Lokasjonsgruppe', {
      minWidth: 180,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value != null ? String(value) : '-'
    }),
    columnFactory.createTextColumn('masterDescription', 'masterDescription', 'Beskrivelse', {
      minWidth: 220,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createBooleanColumn('commonAvailable', 'commonAvailable', 'Fellesgruppe', {
      minWidth: 130,
      sortable: true,
      filterType: 'both',
      formatter: (value: any) => value ? 'Ja' : 'Nei',
      renderer: (value: any) => (
        <span className={`inline-flex items-center gap-1 text-xs font-medium ${value ? 'text-green-700' : 'text-gray-400'}`}>
          {value
            ? <><CheckCircle className="w-3.5 h-3.5" /> Ja</>
            : <><XCircle className="w-3.5 h-3.5" /> Nei</>}
        </span>
      ),
    }),
    columnFactory.createNumberColumn('childCount', 'childCount', 'Antall sub', {
      minWidth: 100,
      sortable: true,
      filterType: 'none',
      formatter: (value: any) => value != null ? String(value) : '0'
    }),
    // ── Audit-kolonner (skjult som standard) ──
    columnFactory.createTextColumn('createdBy', 'createdBy', 'Opprettet av', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      visible: false,
    }),
    columnFactory.createTextColumn('created', 'created', 'Opprettet', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      visible: false,
      formatter: (value: any) => formatDate(value),
    }),
    columnFactory.createTextColumn('updatedBy', 'updatedBy', 'Oppdatert av', {
      minWidth: 140,
      sortable: true,
      filterType: 'both',
      visible: false,
    }),
    columnFactory.createTextColumn('lastUpdate', 'lastUpdate', 'Sist oppdatert', {
      minWidth: 120,
      sortable: true,
      filterType: 'both',
      visible: false,
      formatter: (value: any) => formatDate(value),
    }),
  ], []);

  // Alle unike master-gruppenumre (brukes til å hindre at en master velges som sub)
  const allMasterNos = useMemo(
    () => masterRows.map(r => r.masterBinGrpNo),
    [masterRows]
  );

  // Expandable config — bruker renderDetail for full inline-kontroll
  const expandableConfig: ExpandableRowConfig<MasterGroupRow, ChildRelationRow[]> = useMemo(() => ({
    getDetailData: (row: MasterGroupRow) => getChildRows(row),
    renderDetail: (row: MasterGroupRow, childRows: ChildRelationRow[]) => (
      <SubGroupDetail
        masterRow={row}
        childRows={childRows}
        binGroups={binGroups}
        masterNos={allMasterNos}
        onRefresh={loadData}
      />
    ),
    expandOnRowClick: false,
  }), [getChildRows, binGroups, allMasterNos, loadData]);

  // Valgt master-gruppe for side-panelet (enkeltvalg)
  const selectedMasterNo = selectedIds.length === 1 ? selectedIds[0] : null;

  const handleVedlikehold = useCallback(() => {
    if (selectedIds.length === 0) return;
    if (selectedIds.length > 1) {
      alert('Vedlikehold støtter kun én master-gruppe av gangen. Velg én rad.');
      return;
    }
    setShowSidePanel(true);
  }, [selectedIds.length]);

  const tableActions: TableAction<MasterGroupRow>[] = useMemo(() => [
    {
      id: 'create',
      label: 'Ny relasjon',
      icon: Plus,
      onClick: () => setShowCreateModal(true),
      variant: 'primary' as const
    },
    {
      id: 'vedlikehold',
      label: `Vedlikehold (${selectedIds.length})`,
      icon: Edit,
      onClick: handleVedlikehold,
      disabled: () => selectedIds.length === 0,
      variant: 'primary' as const
    }
  ], [selectedIds.length, handleVedlikehold]);

  // Master-rader som ikke er fellesgruppe — skal vises som advarsel
  const invalidMasters = masterRows.filter(r => !r.commonAvailable);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-gray-50">
      {invalidMasters.length > 0 && (
        <div className="flex items-start gap-2 mx-4 mt-3 px-3 py-2.5 bg-amber-50 border border-amber-300 rounded-lg text-xs text-amber-800">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-amber-600" />
          <div>
            <span className="font-semibold">Advarsel:</span>{' '}
            {invalidMasters.length === 1
              ? `Master-gruppe ${invalidMasters[0].masterBinGrpNo} (${invalidMasters[0].masterDescription || '–'}) er ikke merket som Fellesgruppe.`
              : `${invalidMasters.length} master-grupper er ikke merket som Fellesgruppe: ${invalidMasters.map(r => r.masterBinGrpNo).join(', ')}.`
            }{' '}
            En master MÅ være en Fellesgruppe. Endre dette under Lokasjonsgrupper.
          </div>
        </div>
      )}
      <div className="flex-1 min-h-0">
        <WarehouseTable
          data={masterRows}
          columns={masterColumns}
          config={{
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: false,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: false,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            compactMode: true,
            persistenceKey: 'bin-grp-relations-master-table',
            allowRowSelection: {
              mode: 'multi',
              selectAllMode: 'all-data'
            }
          }}
          selectedRows={selectedIds}
          onSelectionChange={(ids) => setSelectedIds(ids as number[])}
          getRowId={(item) => item.id}
          tableActions={tableActions}
          expandableConfig={expandableConfig}
          loading={isLoading}
          error={error || undefined}
        />
      </div>

      {showSidePanel && selectedMasterNo !== null && (
        <BinGrpRelationSidePanel
          selectedMasterNo={selectedMasterNo}
          data={relations}
          binGroups={binGroups}
          onClose={() => setShowSidePanel(false)}
          onSave={() => {
            loadData();
          }}
        />
      )}

      {showCreateModal && (
        <BinGrpRelationCreateModal
          isOpen={showCreateModal}
          binGroups={binGroups}
          existingRelations={relations}
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            loadData();
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};
